import { motion } from "framer-motion";
import { Facebook, ArrowUpRight } from "lucide-react";
import { BeforeAfter } from "@/components/site/BeforeAfter";

const PAIRS = [
  { before: "/ba-oven-before.jpg", after: "/ba-oven-after.jpg", caption: "Oven clean", alt: "oven" },
  { before: "/ba-door-before.jpg", after: "/ba-door-after.jpg", caption: "Shower screen", alt: "shower screen glass" },
  { before: "/ba-tray-before.jpg", after: "/ba-tray-after.jpg", caption: "Shower tray", alt: "shower tray" },
];

const WORK = [
  { src: "/work-1.jpg", alt: "Freshly cleaned open-plan kitchen and living space" },
  { src: "/work-2.jpg", alt: "Spotless cream cottage-style kitchen" },
  { src: "/work-3.jpg", alt: "Immaculate bathroom with freestanding bath and walk-in shower" },
  { src: "/work-4.jpg", alt: "Beautifully presented bedroom, freshly cleaned" },
];

const SOCIAL_WORK = "https://www.facebook.com/share/1D36HFTaeK/?mibextid=wwXIfr";

export const Gallery = () => (
  <section id="gallery" className="bg-cream py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-blush mb-5">(The proof)</p>
          <h2 className="font-serif font-light text-espresso text-4xl md:text-6xl tracking-tight leading-[1.02]">
            Real homes, <span className="italic text-blush">before & after</span>
          </h2>
        </div>
        <p className="max-w-xs text-muted-foreground font-light">My own work, completely unretouched. Slide across each photo to see the transformation.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {PAIRS.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
          >
            <BeforeAfter
              before={p.before}
              after={p.after}
              beforeAlt={`${p.alt} before cleaning`}
              afterAlt={`${p.alt} after cleaning by Klean With Katie`}
              caption={p.caption}
            />
          </motion.div>
        ))}
      </div>

      {/* Recent finished work */}
      <div className="mt-24 md:mt-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-blush mb-5">(Finished results)</p>
            <h2 className="font-serif font-light text-espresso text-4xl md:text-6xl tracking-tight leading-[1.02]">
              Recent <span className="italic text-blush">work</span>
            </h2>
          </div>
          <p className="max-w-xs text-muted-foreground font-light">A glimpse of homes left sparkling — real spaces, freshly cleaned and beautifully presented.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {WORK.map((w, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.1 }}
              className="group relative overflow-hidden aspect-[3/4] bg-muted"
            >
              <img src={w.src} alt={w.alt} className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/10 transition-colors duration-500" />
            </motion.figure>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href={SOCIAL_WORK} target="_blank" rel="noreferrer" data-testid="work-social-link" className="group inline-flex items-center gap-3 bg-espresso text-cream px-8 py-4 rounded-full text-xs uppercase tracking-[0.22em] hover:bg-blush transition-colors duration-300">
            <Facebook size={16} />
            See all my work on Facebook
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
        </div>
      </div>
    </div>
  </section>
);
